import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Icon, ListGroup, ListRow, Skeleton } from '@uplink/ui';
import { errorText, useTRPC } from '../api.ts';
import { haptic, openPayUrl } from '../telegram.ts';

/** Публичный статус и история пользовательских проверок VPN из России. */
export function AdminVpnStatusScreen() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const status = useQuery(trpc.admin.vpnRussiaStatus.queryOptions());
  const checks = useQuery(trpc.admin.vpnRussiaChecks.queryOptions());
  const remove = useMutation(
    trpc.admin.deleteVpnRussiaCheck.mutationOptions({
      onSuccess: async () => {
        haptic.success();
        await Promise.all([
          queryClient.invalidateQueries(trpc.admin.vpnRussiaChecks.pathFilter()),
          queryClient.invalidateQueries(trpc.admin.vpnRussiaStatus.pathFilter()),
        ]);
      },
      onError: () => haptic.error(),
    }),
  );

  return (
    <main className="ul-screen">
      <h1 className="ul-screen__title">Проверка VPN в России</h1>
      <ListGroup header="Как работает проверка">
        <ListRow
          icon="info"
          title="Тест проверяет реальное подключение Uplink"
          subtitle="Пользователь получает пробный ключ в приложении, подключается к VPN, а страница проверяет IP Uplink, скорость загрузки и доступность популярных сервисов. Без включённого Uplink VPN тест не запускается."
        />
        <ListRow
          icon="globe"
          title="Открыть публичный тест"
          subtitle={`${window.location.origin}/vpn-status`}
          chevron
          onClick={() => openPayUrl(new URL('/vpn-status', window.location.origin).toString())}
        />
      </ListGroup>

      {status.isPending ? (
        <Skeleton height={120} />
      ) : status.data ? (
        <ListGroup header="Последняя проверка">
          <ListRow
            icon={status.data.works ? 'check' : 'alert'}
            tone={status.data.works ? undefined : 'crit'}
            title={status.data.works ? 'VPN работал' : 'VPN не прошёл проверку'}
            subtitle={`${new Date(status.data.checkedAt).toLocaleString('ru-RU')}${status.data.downloadMbps !== null ? ` · ${status.data.downloadMbps} Мбит/с` : ''}`}
          />
        </ListGroup>
      ) : null}

      {checks.isPending ? (
        <Skeleton height={220} />
      ) : checks.data ? (
        <>
          <ListGroup header="Статистика последних 100 проверок из России">
            <ListRow
              icon="check"
              title={`${checks.data.summary.successful} успешных`}
              subtitle={`Всего ${checks.data.summary.total} · не прошли ${checks.data.summary.failed}`}
              value={
                checks.data.summary.averageDownloadMbps === null
                  ? undefined
                  : `${checks.data.summary.averageDownloadMbps} Мбит/с`
              }
            />
          </ListGroup>
          <ListGroup header="История проверок">
            {checks.data.checks.length ? (
              checks.data.checks.map((check) => {
                const services = [
                  ['Telegram', check.telegramWorks],
                  ['YouTube', check.youtubeWorks],
                  ['Instagram', check.instagramWorks],
                  ['TikTok', check.tiktokWorks],
                  ['Discord', check.discordWorks],
                ] as const;
                const unavailable = services.filter(([, available]) => !available).map(([name]) => name);
                return (
                  <ListRow
                    key={check.id}
                    icon={check.works ? 'check' : 'alert'}
                    tone={check.works ? undefined : 'crit'}
                    title={`${check.region} · ${check.downloadMbps ?? '—'} Мбит/с`}
                    subtitle={`${new Date(check.createdAt).toLocaleString('ru-RU')} · ${
                      unavailable.length ? `недоступны: ${unavailable.join(', ')}` : 'все сервисы доступны'
                    }`}
                    trailing={
                      <button
                        type="button"
                        className="ul-icon-btn ul-icon-btn--crit"
                        aria-label="Удалить проверку"
                        disabled={remove.isPending}
                        onClick={() => {
                          if (!window.confirm('Удалить эту проверку? Она перестанет влиять на статистику.'))
                            return;
                          remove.mutate({ id: check.id });
                        }}
                      >
                        <Icon name="trash" size={20} />
                      </button>
                    }
                  />
                );
              })
            ) : (
              <ListRow icon="info" title="Подтверждённых проверок из России пока нет" />
            )}
          </ListGroup>
          {remove.isError ? (
            <div className="ul-error" role="alert">
              {errorText(remove.error)}
            </div>
          ) : null}
        </>
      ) : null}
    </main>
  );
}
